import { handleActions } from 'redux-actions';
import {
  parseOccupancy,
  parseOccupancies,
  getRoomCount,
} from '@airasia-common/libraries/utilities/OccupancyHelper';
import { hotelFilterDefault } from '../../ParamFilter';
import {
  HOTELS_LIST_BATCH_SIZE,
  ENABLE_SORTING_HOTEL_LIST_AT_CLIENT,
} from '../../constants';
import { types } from './filter.meta';

const defaultFilter = {
  sortBy: 'recommended',
  guestRating: '',
  focusId: '',
  aaId: '',
  hotelNameObj: {},
  filterItemId: '',
  amenities: [],
  amenity: [],
  hotelClass: [],
  priceFilter: [],
  paymentType: [],
  propertyType: [],
  promotions: [],
  locationObject: {},
  toggleStatusBanner: false,
};

const initialState = {
  hotel: {
    ...hotelFilterDefault,
    defaultFilter,
    ...defaultFilter,
    filtering: false,
    occupancy: [],
    locationId: '',
    hotelsSize: HOTELS_LIST_BATCH_SIZE,
    guests: {},
    selectedCurrency: {
      value: hotelFilterDefault.currency,
    },
    isInit: false,
    isApplying: false,
    updateAtClient: true,
    hotelClass: [],
    amenities: [],
    amenity: [],
    locationObject: {},
    paymentType: [],
    propertyType: [],
    promotions: [],
    dropoutAlertDisabled: false,
    searchedData: {}, // Need to hold the data or prev data thats being searched
  },
};

function compareObjects(obj1, obj2) {
  let diff = new Set();

  function arraysEqual(a1, a2) {
    if (a1 === null || a2 === null) return false;
    if (a1.length !== a2.length) return false;

    for (let i = 0; i < a1.length; i++) {
      if (a1[i] !== a2[i]) return false;
    }

    return true;
  }

  function isEmpty(obj = {}) {
    if (obj === null) return true;
    return Object.keys(obj).length === 0 && obj.constructor === Object;
  }

  // this will help to determine what filter been applied and removed by checking what object has changed
  function findDifferences(o1, o2, path = '') {
    const keys1 = o1 ? Object.keys(o1) : [];
    const keys2 = o2 ? Object.keys(o2) : [];

    keys1 &&
      keys1.length &&
      keys1.forEach(key => {
        const currentPath = path ? `${path}.${key}` : key;

        if (
          o1 &&
          o2 &&
          typeof o1[key] === 'object' &&
          o1[key] !== null &&
          !Array.isArray(o1[key])
        ) {
          findDifferences(o1[key], o2[key], currentPath);
        } else if (
          !o2 ||
          // eslint-disable-next-line no-prototype-builtins
          !o2.hasOwnProperty(key) ||
          (!Array.isArray(o1[key]) && o1[key] !== o2[key]) ||
          (Array.isArray(o1[key]) && !arraysEqual(o1[key], o2[key]))
        ) {
          if (
            !Array.isArray(o1[key]) ||
            o1[key].length !== 0 ||
            (o2 && o2[key] && o2[key].length !== 0)
          ) {
            if (!isEmpty(o1[key]) || (o2 && o2[key] && !isEmpty(o2[key]))) {
              diff.add(currentPath.split('.')[0]);
            }
          }
        }
      });

    keys2 &&
      keys2.length &&
      keys2.forEach(key => {
        // eslint-disable-next-line no-prototype-builtins
        if (!o1 || !o1.hasOwnProperty(key)) {
          const currentPath = path ? `${path}.${key}` : key;
          if (!Array.isArray(o2[key]) || o2[key].length !== 0) {
            if (!isEmpty(o2[key])) {
              diff.add(currentPath.split('.')[0]);
            }
          }
        }
      });
  }

  findDifferences(obj1, obj2);
  return Array.from(diff);
}

function shouldUpdateClientOrServerSide(keys) {
  if (keys.length === 0) {
    return false;
  }

  const filterKeyNames = [
    'guestRating',
    'aaId',
    'locationId',
    'amenities',
    'amenity',
    'hotelClass',
    'paymentType',
    'priceFilter',
    'checkin',
    'checkout',
    'guests',
    'roomsCount',
    'sortBy',
    'promotions',
    'isApplying',
    'toggleStatusBanner',
  ];

  const clientSideFilters = [
    'promotions',
    'priceFilter',
    'sortBy',
    'toggleStatusBanner',
  ];
  let hasClientSide = false;
  let hasServerSide = false;

  for (const key of keys) {
    if (clientSideFilters.includes(key)) {
      hasClientSide = true;
    } else if (filterKeyNames.includes(key)) {
      hasServerSide = true;
    }

    if (hasClientSide && hasServerSide) {
      break;
    }
  }

  return hasClientSide && !hasServerSide;
}

function initHotelFilter(state, { payload }) {
  if (state.hotel.isInit) {
    return state;
  }
  const hotel = { ...state.hotel, ...payload };
  const { occupancy, sortBy, guestRating } = hotel;
  const guests = Array.isArray(occupancy)
    ? parseOccupancies(occupancy)
    : parseOccupancy(occupancy);

  const isApplying =
    sortBy !== defaultFilter.sortBy ||
    guestRating !== defaultFilter.guestRating;
  const updateAtClient =
    state.hotel.sortBy !== payload.sortBy &&
    state.hotel.guestRating === payload.guestRating;

  state.hotel.aaId = hotel.aaId;
  const roomsCount = getRoomCount(occupancy);
  return {
    ...state,
    hotel: {
      ...hotel,
      guests,
      isInit: true,
      isApplying,
      roomsCount,
      updateAtClient,
    },
  };
}

function updateBigPointFilter(state, { payload }) {
  const hotel = { ...state.hotel, ...payload };
  return {
    ...state,
    hotel: { ...hotel },
  };
}

function disableDropoutAlert(state) {
  return {
    ...state,
    hotel: { ...state.hotel, dropoutAlertDisabled: true },
  };
}

function arrayEquals(arrayFirst, arraySecond) {
  return (
    Array.isArray(arrayFirst) &&
    Array.isArray(arraySecond) &&
    arrayFirst.length === arraySecond.length &&
    arrayFirst.every((val, index) => val === arraySecond[index])
  );
}

function updateHotelFilter(state, { payload }) {
  if (!payload) {
    return clearHotelFilter(state);
  }

  const hotel = { ...state.hotel, ...payload };
  const {
    sortBy,
    guestRating,
    aaId,
    hotelClass,
    priceFilter,
    paymentType,
    neighbourhoodFilterSelected = false,
    amenity,
    propertyType,
    promotions,
  } = hotel;
  const isApplying =
    sortBy !== defaultFilter.sortBy ||
    guestRating !== defaultFilter.guestRating ||
    (hotelClass.length > 0
      ? !arrayEquals(hotelClass, defaultFilter.hotelClass)
      : false) ||
    (propertyType.length > 0
      ? !arrayEquals(propertyType, defaultFilter.propertyType)
      : false) ||
    (paymentType.length > 0
      ? !arrayEquals(paymentType, defaultFilter.paymentType)
      : false) ||
    priceFilter.length !== 0 ||
    amenity.length !== 0 ||
    aaId !== defaultFilter.aaId ||
    promotions.length !== 0 ||
    neighbourhoodFilterSelected;

  const difference = compareObjects(state.hotel, payload);
  const shouldUpdateClientOrWithServerSide = shouldUpdateClientOrServerSide(
    difference
  );

  return {
    ...state,
    hotel: {
      ...hotel,
      isApplying,
      filtering: false,
      updateAtClient: shouldUpdateClientOrWithServerSide,
    },
  };
}

function applyingHotelFilter(state) {
  return { ...state, hotel: { ...state.hotel, filtering: true } };
}

function clearHotelFilter(state) {
  return {
    ...state,
    hotel: {
      ...state.hotel,
      ...defaultFilter,
      // we dont need to clear the toggleStatus, the reason its not part of normal filter. We are depending on toggle only
      toggleStatusBanner: state.hotel?.toggleStatusBanner,
      ...{ sortBy: state.hotel.sortBy },
      isApplying: false,
      updateAtClient: false,
    },
  };
}

function initHotelBookingFilter(state, { payload }) {
  if (state.hotel.isInit) {
    return state;
  }

  const hotel = { ...state.hotel, ...payload };

  return {
    ...state,
    hotel: {
      ...hotel,
      isInit: true,
    },
  };
}

function updateClientFilter(state, { payload }) {
  const hotel = { ...state.hotel, ...payload };

  return {
    ...state,
    hotel: {
      ...hotel,
      isInit: true,
    },
  };
}

export default handleActions(
  {
    [types.UPDATE_HOTEL_FILTER_COMPLETED]: updateHotelFilter,
    [types.INIT_HOTEL_FILTER_COMPLETED]: initHotelFilter,
    [types.INIT_HOTEL_BOOKING_FILTER]: initHotelBookingFilter,
    [types.UPDATE_CLIENT_FILTER]: updateClientFilter,
    [types.UPDATE_BIG_POINTS]: updateBigPointFilter,
    [types.DISABLE_DROPOUT_ALERT]: disableDropoutAlert,
    [types.FILTER_APPLYING]: applyingHotelFilter,
  },
  initialState
);
